const PAYMENT_TONE_STORAGE_KEY = 'cor:payment-tone-enabled';

export const browserAlertsSupported = () =>
  typeof window !== 'undefined' && typeof Notification !== 'undefined';

export const getBrowserAlertPermission = (): NotificationPermission | 'unsupported' => {
  if (!browserAlertsSupported()) {
    return 'unsupported';
  }

  return Notification.permission;
};

export const requestBrowserAlertPermission = async (): Promise<NotificationPermission | 'unsupported'> => {
  if (!browserAlertsSupported()) {
    return 'unsupported';
  }

  return Notification.requestPermission();
};

export const getPaymentToneEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return true;
  }

  const storedValue = window.localStorage.getItem(PAYMENT_TONE_STORAGE_KEY);
  return storedValue !== 'false';
};

export const setPaymentToneEnabled = (enabled: boolean): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(PAYMENT_TONE_STORAGE_KEY, enabled ? 'true' : 'false');
};

export const sendBrowserAlert = (title: string, body: string, tag: string): void => {
  if (!browserAlertsSupported() || Notification.permission !== 'granted') {
    return;
  }

  try {
    const notification = new Notification(title, {
      body,
      tag,
      renotify: true,
      silent: false,
    });

    window.setTimeout(() => notification.close(), 10000);
  } catch (error) {
    console.error('Failed to send browser alert', error);
  }
};

export const playPaymentTone = async (): Promise<void> => {
  if (typeof window === 'undefined' || !getPaymentToneEnabled()) {
    return;
  }

  const AudioContextCtor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) {
    return;
  }

  try {
    const audioContext = new AudioContextCtor();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
    oscillator.frequency.setValueAtTime(660, audioContext.currentTime + 0.12);

    gainNode.gain.setValueAtTime(0.0001, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.15, audioContext.currentTime + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.32);

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.34);

    window.setTimeout(() => {
      void audioContext.close().catch(() => undefined);
    }, 500);
  } catch (error) {
    console.error('Failed to play payment tone', error);
  }
};
